import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
// import logo from 'images/Circle.gif';
// import darkLogo from 'images/dark logo.png';
// import companyName from 'images/Shift.png';
import { getPostLoginRedirectPath, handleSubmission, submission } from 'Auth';
import { BASE_URL } from 'BaseURL';
import { useLocation, useNavigate } from 'react-router-dom';
import { themeContext } from 'contexts/theme_context/ThemeContext';
import { languageContext } from 'contexts/language_context/LanguageContext';
import { AuthContext } from 'contexts/auth_context/AuthContext';
import {
  buildAwsWafRequestConfig,
  getAwsWafCaptchaLocale,
  getAwsWafToken,
  isAwsWafCaptchaConfigured,
  loadAwsWafCaptchaScript,
  renderAwsWafCaptcha,
  shouldRequireAwsWafCaptcha,
} from 'services/awsWafCaptcha';
import './AuthShared.css';

const DEFAULT_CAPTCHA_COPY = {
  title: 'Human verification',
  loading: 'Preparing sign in protection...',
  ready: 'Complete verification to continue.',
  solved: 'Verification complete. You can sign in now.',
  incorrect: 'That answer did not pass. Try the new puzzle.',
  expired: 'The verification expired. Complete the new puzzle before signing in.',
  unavailable: 'Sign in protection is unavailable. Refresh the page or contact support.',
  action: 'Verify',
  verifiedAction: 'Verified',
  modalTitle: 'Complete verification',
  close: 'Close verification',
};

const CAPTCHA_STATUS_META = {
  loading: {
    icon: 'fa-shield-halved',
    tone: 'pending',
    messageKey: 'loading',
  },
  ready: {
    icon: 'fa-shield-halved',
    tone: 'pending',
    messageKey: 'ready',
  },
  solved: {
    icon: 'fa-circle-check',
    tone: 'success',
    messageKey: 'solved',
  },
  incorrect: {
    icon: 'fa-triangle-exclamation',
    tone: 'warning',
    messageKey: 'incorrect',
  },
  expired: {
    icon: 'fa-clock',
    tone: 'warning',
    messageKey: 'expired',
  },
  error: {
    icon: 'fa-triangle-exclamation',
    tone: 'error',
    messageKey: 'unavailable',
  },
  unconfigured: {
    icon: 'fa-triangle-exclamation',
    tone: 'error',
    messageKey: 'unavailable',
  },
};

const getCaptchaCopy = (loginCopy = {}) => ({
  ...DEFAULT_CAPTCHA_COPY,
  ...(loginCopy.captcha || {}),
});

const getCaptchaStatusMeta = (status) => (
  CAPTCHA_STATUS_META[status] || CAPTCHA_STATUS_META.ready
);

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const captchaContainerRef = useRef(null);
  const captchaRenderedRef = useRef(false);
  const [loading, setLoading] = useState(false);
  const [passwordType, setPasswordType] = useState('password');
  const [user, setUser] = useState({});
  const [errors, setErrors] = useState({});
  const [captchaModalOpen, setCaptchaModalOpen] = useState(false);
  const [captchaScriptReady, setCaptchaScriptReady] = useState(false);
  const [captchaStatus, setCaptchaStatus] = useState(() => (
    shouldRequireAwsWafCaptcha() ? 'loading' : 'disabled'
  ));
  const { webtheme } = useContext(themeContext);
  const { weblanguage, jsonfile } = useContext(languageContext);
  const { setAuth, createCookie } = useContext(AuthContext);
  const [data, setData] = useState(() => jsonfile[weblanguage]?.login || {});
  const captchaRequired = shouldRequireAwsWafCaptcha();
  const captchaCopy = useMemo(() => getCaptchaCopy(data), [data]);
  const captchaStatusMeta = getCaptchaStatusMeta(captchaStatus);
  const captchaStatusMessage = captchaCopy[captchaStatusMeta.messageKey];

  const resetCaptchaContainer = useCallback(() => {
    if (captchaContainerRef.current) {
      captchaContainerRef.current.replaceChildren();
    }
    captchaRenderedRef.current = false;
  }, []);

  useEffect(() => {
    setData(jsonfile[weblanguage]?.login || {});
  }, [weblanguage, jsonfile]);

  useEffect(() => () => {
    resetCaptchaContainer();
  }, [resetCaptchaContainer]);

  useEffect(() => {
    let isMounted = true;

    if (!captchaRequired) {
      return () => {
        isMounted = false;
      };
    }

    if (!isAwsWafCaptchaConfigured()) {
      setCaptchaStatus('unconfigured');
      setErrors((currentErrors) => ({
        ...currentErrors,
        general: captchaCopy.unavailable,
      }));
      return () => {
        isMounted = false;
      };
    }

    loadAwsWafCaptchaScript()
      .then(() => {
        if (!isMounted) return;
        setCaptchaScriptReady(true);
        setCaptchaStatus((currentStatus) => (
          currentStatus === 'loading' ? 'ready' : currentStatus
        ));
      })
      .catch(() => {
        if (!isMounted) return;
        setCaptchaStatus('error');
        setErrors((currentErrors) => ({
          ...currentErrors,
          general: captchaCopy.unavailable,
        }));
      });

    return () => {
      isMounted = false;
    };
  }, [captchaCopy.unavailable, captchaRequired]);

  useEffect(() => {
    let isMounted = true;

    if (
      !captchaRequired
      || !captchaModalOpen
      || !captchaScriptReady
      || captchaRenderedRef.current
      || !captchaContainerRef.current
    ) {
      return () => {
        isMounted = false;
      };
    }

    try {
      renderAwsWafCaptcha(captchaContainerRef.current, {
        onLoad: () => {
          if (!isMounted) return;
          setCaptchaStatus((currentStatus) => (
            currentStatus === 'solved' ? currentStatus : 'ready'
          ));
        },
        onSuccess: () => {
          if (!isMounted) return;
          setCaptchaStatus('solved');
          resetCaptchaContainer();
          setCaptchaModalOpen(false);
          setErrors((currentErrors) => {
            const nextErrors = { ...currentErrors };
            if (
              nextErrors.general === captchaCopy.ready
              || nextErrors.general === DEFAULT_CAPTCHA_COPY.ready
            ) {
              delete nextErrors.general;
            }
            return nextErrors;
          });
        },
        onPuzzleCorrect: () => {
          if (!isMounted) return;
          setCaptchaStatus('ready');
        },
        onPuzzleIncorrect: () => {
          if (!isMounted) return;
          setCaptchaStatus('incorrect');
        },
        onPuzzleTimeout: () => {
          if (!isMounted) return;
          setCaptchaStatus('expired');
        },
        onError: () => {
          if (!isMounted) return;
          setCaptchaStatus('error');
          setErrors((currentErrors) => ({
            ...currentErrors,
            general: captchaCopy.unavailable,
          }));
        },
      }, {
        defaultLocale: getAwsWafCaptchaLocale(weblanguage),
      });
      captchaRenderedRef.current = true;
    } catch {
      setCaptchaStatus('error');
      setErrors((currentErrors) => ({
        ...currentErrors,
        general: captchaCopy.unavailable,
      }));
    }

    return () => {
      isMounted = false;
    };
  }, [captchaCopy, captchaModalOpen, captchaRequired, captchaScriptReady, resetCaptchaContainer, weblanguage]);

  useEffect(() => {
    if (!captchaModalOpen) return undefined;

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setCaptchaModalOpen(false);
      }
    };

    window.addEventListener('keydown', handleEscape);
    return () => {
      window.removeEventListener('keydown', handleEscape);
    };
  }, [captchaModalOpen]);

  const openCaptchaModal = () => {
    if (!captchaRequired || captchaStatus === 'solved' || captchaStatus === 'error' || captchaStatus === 'unconfigured') {
      return;
    }
    setCaptchaModalOpen(true);
  };

  const closeCaptchaModal = useCallback(() => {
    resetCaptchaContainer();
    setCaptchaModalOpen(false);
  }, [resetCaptchaContainer]);

  const navigateAfterLogin = useCallback((successPath) => {
    closeCaptchaModal();
    navigate(successPath);
  }, [closeCaptchaModal, navigate]);

  const handleFormSubmit = async (e) => {
    e.preventDefault();

    if (captchaRequired && captchaStatus !== 'solved') {
      setErrors((currentErrors) => ({
        ...currentErrors,
        general: captchaCopy.ready,
      }));
      setCaptchaModalOpen(true);
      return;
    }

    let requestConfig;
    if (captchaRequired) {
      setLoading(true);
      try {
        const wafToken = await getAwsWafToken();
        requestConfig = buildAwsWafRequestConfig(wafToken);
      } catch {
        setLoading(false);
        setErrors((currentErrors) => ({
          ...currentErrors,
          general: captchaCopy.expired,
        }));
        setCaptchaStatus('expired');
        setCaptchaModalOpen(true);
        return;
      }
      setLoading(false);
    }

    closeCaptchaModal();
    submission(
      e,
      user,
      `${BASE_URL}auth/token/`,
      setErrors,
      navigateAfterLogin,
      'choice',
      setLoading,
      setAuth,
      createCookie,
      requestConfig,
      getPostLoginRedirectPath(location.state?.from)
    );
  };

  const handleInputChange = (e) => {
    handleSubmission(e.target, user, setUser);
  };

  const togglePasswordVisibility = () => {
    setPasswordType(passwordType === 'password' ? 'text' : 'password');
  };

  const logoSrc = webtheme === 'light' ? '/Wordmark/Shift Wordmark Black.svg' : '/Wordmark/Shift Wordmark White.svg';
  const themeContainerClass = webtheme === 'dark' ? 'dark-theme-bg' : '';
  const themeCardClass = webtheme === 'dark' ? 'dark-theme-card' : '';
  const isRtl = weblanguage === 'ar';

  return (
    <div className={`auth-container ${themeContainerClass}`} dir={isRtl ? 'rtl' : 'ltr'}>
      <div className={`auth-card auth-login-card ${themeCardClass}`}>
        <div className="auth-header">
          <img
            src={logoSrc}
            alt="Shift EV"
            className="auth-logo"
          />
        </div>

        {data && (
          <form className="auth-form" onSubmit={handleFormSubmit}>
            {errors.general && <p className="auth-error auth-error-banner">{errors.general}</p>}

            <div className="auth-form-group">
              <label htmlFor="username" className="auth-label">
                {data.username}
              </label>
              <input
                type="text"
                id="username"
                name="username"
                autoComplete="username"
                placeholder={data.username}
                className="auth-input"
                onChange={handleInputChange}
              />
              {errors.username && <p className="auth-error">{errors.username}</p>}
            </div>

            <div className="auth-form-group">
              <label htmlFor="password" className="auth-label">
                {data.password}
              </label>
              <div className="auth-input-wrapper">
                <input
                  type={passwordType}
                  id="password"
                  name="password"
                  autoComplete="current-password"
                  placeholder={data.password}
                  className="auth-input auth-input-password"
                  onChange={handleInputChange}
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={togglePasswordVisibility}
                  aria-label={passwordType === 'password' ? data.show : data.hide}
                  title={passwordType === 'password' ? data.show : data.hide}
                >
                  <i className={`fa-solid ${passwordType === 'password' ? 'fa-eye' : 'fa-eye-slash'}`}></i>
                </button>
              </div>
              {errors.password && <p className="auth-error">{errors.password}</p>}
            </div>

            <div className="d-flex justify-content-end mb-4">
              <button
                type="button"
                className="auth-link"
                style={{ background: 'none', border: 'none', padding: 0 }}
                onClick={() => navigate('/reset/password')}
              >
                {data.forget}
              </button>
            </div>

            {captchaRequired && (
              <>
                <div
                  className={`auth-form-group auth-captcha-group auth-captcha-panel auth-captcha-panel-${captchaStatusMeta.tone}`}
                  role="group"
                  aria-labelledby="auth-captcha-title"
                >
                  <button
                    type="button"
                    className={`auth-captcha-check auth-captcha-check-${captchaStatusMeta.tone}`}
                    role="checkbox"
                    aria-checked={captchaStatus === 'solved'}
                    aria-label={captchaStatus === 'solved' ? captchaCopy.verifiedAction : captchaCopy.action}
                    onClick={openCaptchaModal}
                    disabled={captchaStatus === 'solved' || captchaStatus === 'error' || captchaStatus === 'unconfigured'}
                  >
                    <span className="auth-captcha-checkbox" aria-hidden="true">
                      <i className={`fa-solid ${captchaStatusMeta.icon}`}></i>
                    </span>
                  </button>
                  <div className="auth-captcha-copy">
                    <p id="auth-captcha-title" className="auth-captcha-title">
                      {captchaCopy.title}
                    </p>
                    <p className="auth-captcha-status" aria-live="polite">
                      {captchaStatusMessage}
                    </p>
                  </div>
                  <button
                    type="button"
                    className="auth-captcha-action"
                    onClick={openCaptchaModal}
                    disabled={captchaStatus === 'solved' || captchaStatus === 'error' || captchaStatus === 'unconfigured'}
                  >
                    {captchaStatus === 'solved' ? captchaCopy.verifiedAction : captchaCopy.action}
                  </button>
                </div>

                {captchaModalOpen && (
                  <div
                    className="auth-captcha-modal auth-captcha-modal-open"
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="auth-captcha-modal-title"
                  >
                    <div className="auth-captcha-modal-backdrop" aria-hidden="true" />
                    <div className="auth-captcha-modal-card">
                      <div className="auth-captcha-modal-header">
                        <div className="auth-captcha-modal-heading">
                          <span
                            className={`auth-captcha-icon auth-captcha-icon-${captchaStatusMeta.tone}`}
                            aria-hidden="true"
                          >
                            <i className={`fa-solid ${captchaStatusMeta.icon}`}></i>
                          </span>
                          <div className="auth-captcha-copy">
                            <p id="auth-captcha-modal-title" className="auth-captcha-title">
                              {captchaCopy.modalTitle}
                            </p>
                            <p className="auth-captcha-status" aria-live="polite">
                              {captchaStatusMessage}
                            </p>
                          </div>
                        </div>
                        <button
                          type="button"
                          className="auth-captcha-modal-close"
                          aria-label={captchaCopy.close}
                          title={captchaCopy.close}
                          onClick={closeCaptchaModal}
                        >
                          <i className="fa-solid fa-xmark"></i>
                        </button>
                      </div>
                      <div className="auth-captcha-widget-shell">
                        <div
                          ref={captchaContainerRef}
                          className="auth-captcha-container"
                        />
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}

            <button
              type="submit"
              className="auth-btn"
              disabled={loading || (captchaRequired && captchaStatus !== 'solved')}
            >
              {loading ? <i className="fa fa-spinner fa-spin"></i> : data.login}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
